import { useState } from 'react';
import supabase from '../lib/supabase';

const MONTHLY_PRICE = process.env.REACT_APP_STRIPE_PRICE_MONTHLY;
const YEARLY_PRICE = process.env.REACT_APP_STRIPE_PRICE_YEARLY;
const FUNCTIONS_URL = process.env.REACT_APP_SUPABASE_URL + '/functions/v1/create-checkout';
const PROXY_URL = process.env.REACT_APP_SUPABASE_URL + '/functions/v1/claude-proxy';

function UpgradeScreen({ onBack }) {
  const [loading, setLoading] = useState(null); // 'monthly' | 'yearly' | null
  const [error, setError] = useState(null);
  const [betaCode, setBetaCode] = useState('');
  const [betaLoading, setBetaLoading] = useState(false);
  const [betaSuccess, setBetaSuccess] = useState(false);

  async function handleBetaCode() {
    if (!betaCode.trim()) return;
    setBetaLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setError('Please log in first.'); setBetaLoading(false); return; }

      const res = await fetch(PROXY_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ type: 'validate_beta', payload: { code: betaCode.trim() } }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong.'); setBetaLoading(false); return; }
      setBetaSuccess(true);
      setTimeout(() => window.location.reload(), 1500);
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    }
    setBetaLoading(false);
  }

  async function handleCheckout(plan) {
    setLoading(plan);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setError('Please log in first.'); setLoading(null); return; }

      const priceId = plan === 'yearly' ? YEARLY_PRICE : MONTHLY_PRICE;
      const res = await fetch(FUNCTIONS_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ priceId }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        setError(data.error || 'Could not start checkout.');
      }
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    }
    setLoading(null);
  }

  return (
    <div className="upgrade-screen">
      <div className="upgrade-icon">🎓</div>
      <h2 className="upgrade-title">Your GED is closer than you think</h2>
      <p className="upgrade-sub">Unlock unlimited practice, a personal AI Tutor, and full mock exams — everything you need to pass with confidence.</p>

      <div className="plan-cards">
        <div className="plan-card plan-card--highlight">
          <div className="plan-badge">Best Value — Save 40%</div>
          <p className="plan-name">Yearly Plan</p>
          <p className="plan-price">$5.99<span>/mo</span></p>
          <p className="plan-note">$5.99/mo when billed annually · $71.88/year</p>
          <button
            className="upgrade-cta"
            onClick={() => handleCheckout('yearly')}
            disabled={!!loading}
          >
            {loading === 'yearly' ? 'Redirecting…' : 'Start Yearly — Best Value'}
          </button>
        </div>
        <div className="plan-card">
          <p className="plan-name">Monthly Plan</p>
          <p className="plan-price">$9.99<span>/mo</span></p>
          <p className="plan-note">Cancel anytime · No commitment</p>
          <button
            className="upgrade-cta upgrade-cta--secondary"
            onClick={() => handleCheckout('monthly')}
            disabled={!!loading}
          >
            {loading === 'monthly' ? 'Redirecting…' : 'Start Monthly — $9.99'}
          </button>
        </div>
      </div>

      <div className="upgrade-why">
        <p className="upgrade-why-heading">Why upgrade?</p>
        <ul className="upgrade-features">
          <li>✓ Unlimited AI-generated questions — never run out of practice</li>
          <li>✓ Personal AI Tutor explains every wrong answer instantly</li>
          <li>✓ Full 40-question mock exam + AI essay grading</li>
          <li>✓ All 26 topics across all 4 GED subjects</li>
          <li>✓ Detailed progress tracking across every topic</li>
        </ul>
      </div>

      <p className="upgrade-objection">
        <strong>Is it worth it?</strong> The GED can increase your earning potential by $9,000+ per year.
        At $9.99/month, that's a return of over 75× in your first year alone.
      </p>

      {error && <p className="auth-error" style={{ marginTop: 16 }}>{error}</p>}

      <button className="back-link" onClick={onBack}>Continue with 5 free questions/day</button>

      <div className="beta-section">
        <p className="beta-label">Have a beta access code?</p>
        {betaSuccess ? (
          <p className="beta-success">✓ Premium unlocked! Reloading…</p>
        ) : (
          <div className="beta-input-row">
            <input
              className="beta-input"
              type="text"
              placeholder="Enter code"
              value={betaCode}
              onChange={e => setBetaCode(e.target.value)}
            />
            <button className="beta-btn" onClick={handleBetaCode} disabled={betaLoading}>
              {betaLoading ? '…' : 'Apply'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default UpgradeScreen;
